/**
 * THE ONE FILE with the wire types of `/api/vn/adm/product-media/**` (the photos and the video of a size).
 *
 * It mirrors, field for field, `apps/vn/.../adm/media/model/ProductMediaModels.java`. When the vn OpenAPI client is regenerated into `hx-services`
 * (`vn-api`), this file is the one to swap: replace the interfaces by the generated models (the names of the fields are the same) and keep `ProductMediaHttp`
 * (`media-http.service.ts`) as the adapter, or point it at the generated resource service. Nothing else in the folder knows the wire.
 *
 * A field that is `null` on the server is left out of the JSON, so the optional ones here are `?`.
 */
import { UiLabel } from 'hx-services';

export type MediaKind = 'PHOTO' | 'VIDEO';

export type MediaStatus = 'UPLOADING' | 'PROCESSING' | 'READY' | 'FAILED';

/** A frame: a 375:402 rectangle in pixels of the auto-oriented original; `x`, `y` = the top left corner. */
export interface MediaCrop {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * One item of the gallery. `src` / `videoSrc` are TEMPLATES with a literal `{w}` (put a width of `widths` / `videoWidths` in); they are there once the item is
 * published (also while it is being rendered again: the old files stay). `previewUrl` (the whole picture, or the chosen frame of a video) and `stripUrl`
 * (a video: one frame per second) are there once the job has read the original; they need the login of adm (an `<img>` cannot send it: they are fetched as blobs).
 * `replacement`: the new file the editor sent to replace this item (the item stays live until it is READY); never nested further.
 */
export interface MediaItem {
  id: number;
  type: MediaKind;
  position: number;
  status: MediaStatus;
  /** The error key of a FAILED item (`media.*`), with the tail of the tool's message after a space. */
  error?: string | null;
  /** PROCESSING for too long: shown as «Обработка задерживается». */
  delayed: boolean;
  fileName?: string | null;
  /** The size of the original in bytes (`hc_files.size_`), as declared at the start and verified at finish. */
  fileSize?: number | null;
  width?: number | null;
  height?: number | null;
  durationMs?: number | null;
  crop?: MediaCrop | null;
  posterMs?: number | null;
  /** A photo whose short side is 600–1199 px: accepted, but soft on a big screen. */
  warnLowRes: boolean;
  src?: string | null;
  widths?: number[] | null;
  videoSrc?: string | null;
  videoWidths?: number[] | null;
  previewUrl?: string | null;
  stripUrl?: string | null;
  replacement?: MediaItem | null;
}

/** What the page needs to know before it lets an editor choose a file. */
export interface MediaLimits {
  photos: number;
  videos: number;
  videoSeconds: number;
  photoBytes: number;
  videoBytes: number;
  photoMinSide: number;
  photoGoodSide: number;
  videoMinSide: number;
}

/** The picture the size has today (read-only «Старое фото»): only when the size has no published item. `url` is a path of the public bucket. */
export interface MediaLegacyPhoto {
  url: string;
}

/** The gallery of a size. `enabled` false = the feature is off on this server: the page shows the items but cannot change them. */
export interface MediaList {
  enabled: boolean;
  productInfoId: number;
  items: MediaItem[];
  limits: MediaLimits;
  legacyPhoto?: MediaLegacyPhoto | null;
}

/** The numbers of one size for the list «Взять из другого размера»: items that count against the limits, and how many of them are READY. */
/** Another size of a card that has this size, with its numbers (`GET /siblings?product_info_id=`); `title` is all languages, absent when neither the product nor the card names it. */
export interface MediaSibling {
  productInfoId: number;
  cardId: number;
  title?: UiLabel;
  photos: number;
  videos: number;
  ready: number;
}

/** The other sizes of the cards that contain a size, in the order of the cards and of the sizes in them; the size itself is not among them. */
export interface MediaSiblings {
  productInfoId: number;
  sizes: MediaSibling[];
}

/**
 * Start an upload. `contentType` is the type adm SNIFFED from the first bytes (Chrome gives a HEIC file an empty type); `size` is the exact size of the file in bytes;
 * `replaceId` = the item this file replaces when it is READY (the old one stays live until then).
 */
export interface UploadMediaRequest {
  productInfoId: number;
  type: MediaKind;
  fileName: string;
  contentType: string;
  size: number;
  replaceId?: number;
}

/** The item that was made and where to send the file. `uploadUrl` is a secret of this editor: it is never logged, stored or shown. */
export interface UploadMedia {
  id: number;
  uploadUrl: string;
  expiresAt: string;
}

/** A new frame for an item; `posterMs` = the cover time of a video (milliseconds; empty = the default). */
export interface MediaFrameRequest {
  crop: MediaCrop;
  posterMs?: number;
}

/** The new order of a size: exactly the ids of its live items that are not a pending replacement. */
export interface MediaOrderRequest {
  productInfoId: number;
  ids: number[];
}

/** Take the items of another size (`fromProductInfoId`) instead of the ones of `toProductInfoId`. */
export interface MediaCopyRequest {
  fromProductInfoId: number;
  toProductInfoId: number;
}

/** What a copy did: items the target got, items of the source left out (not READY yet, or a pending replacement). */
export interface MediaCopy {
  copied: number;
  skipped: number;
}

/** A business error: HTTP 570 `{message, data}` with a key `media.*`. */
export interface MediaError {
  message: string;
  data?: Record<string, unknown>;
}

export const MEDIA_API_BASE = '/api/vn/adm/product-media';

/** The kinds of the preview of an item (`GET /{id}/preview?kind=`). */
export type MediaPreviewKind = 'frame' | 'strip';
